import { useEffect, useMemo, useState } from 'react';

import { faCheck } from '@awesome.me/kit-379289146f/icons/classic/solid';
import { AccessTextField } from '@changegps/horizon-component-library';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, Switch, Typography, useTheme } from '@mui/material';

import {
  BodyContainer,
  ButtonContainer,
  CardBody,
  CardDescription,
  CardGst,
  CardPrice,
  CardTitle,
  CardTitleType,
  CardUsers,
} from '@/features/authentication/features/subscription/components';
import { ProductDetailsType } from '@/features/authentication/features/subscription/store';

import { Subscription } from '../types';

type ProductCardProps = ProductDetailsType & {
  length: number;
  active: number;
  selectedSubscription?: Subscription | null;
  onSelectionChange: (subscription: Subscription | null) => void;
};

export const ProductCard = (props: ProductCardProps) => {
  const theme = useTheme();

  const {
    users,
    type,
    description,
    price,
    body,
    benefits,
    product_id,
    tier_id,
    selectedSubscription,
    onSelectionChange,
  } = props;

  const formatMax = (value: string) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
    return numericValue || 1;
  };

  const isCurrentlySelected = useMemo(() => {
    if (!selectedSubscription) return false;
    return (
      selectedSubscription.product_id === Number(product_id) &&
      selectedSubscription.tier_id === Number(tier_id)
    );
  }, [selectedSubscription, product_id, tier_id]);

  const [licenseCount, setLicenseCount] = useState(() => {
    if (isCurrentlySelected && selectedSubscription?.licence_count) {
      return selectedSubscription.licence_count;
    }
    return formatMax(users.max);
  });

  useEffect(() => {
    if (isCurrentlySelected && selectedSubscription?.licence_count) {
      setLicenseCount(selectedSubscription.licence_count);
    } else if (!isCurrentlySelected) {
      setLicenseCount(formatMax(users.max));
    }
  }, [isCurrentlySelected, selectedSubscription?.licence_count, users.max]);

  const isEnterprise = useMemo(
    () => type.toLowerCase().includes('enterprise'),
    [type],
  );

  const isSmaller = useMemo(
    () => description.toLowerCase().includes('smaller'),
    [description],
  );

  const formatUserCount = useMemo(() => {
    if (!users.min || users.min === '0') {
      return users.max === '3+' || users.max?.includes('+')
        ? ` ${users.max} users`
        : ` Up to ${users.max} users`;
    }

    if (users.max && users.max !== '0' && !users.max.includes('+')) {
      return ` ${users.min} - ${users.max} users`;
    }

    return ` ${users.min}+ users`;
  }, [users.min, users.max]);

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      const productID = Number(product_id);
      const tierID = Number(tier_id);

      const newSubscription: Subscription = {
        product_id: productID,
        tier_id: tierID,
        licence_count: isEnterprise ? licenseCount : Number(users.max),
      };
      onSelectionChange(newSubscription);
    } else {
      onSelectionChange(null);
    }
  };

  const handleLicenseCountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newCount = parseInt(event.target.value);
    setLicenseCount(newCount);

    if (isCurrentlySelected) {
      const productID = Number(product_id);
      const tierID = Number(tier_id);

      const updatedSubscription: Subscription = {
        product_id: productID,
        tier_id: tierID,
        licence_count: newCount,
      };
      onSelectionChange(updatedSubscription);
    }
  };

  const getBodyItemColor = (item: string) => {
    if (isSmaller && (props.length !== 2 || !item.includes(':'))) {
      return '#3D4D54';
    }
    return theme.palette.primary.dark;
  };

  return (
    <Stack>
      <CardTitleType>
        <CardTitle>{type}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardTitleType>

      <CardBody>
        <Stack direction="row">
          <CardPrice>${price}</CardPrice>
          <CardGst>+ GST{isEnterprise && ' Per person'}</CardGst>
        </Stack>

        <CardUsers>Monthly | 12-month contract |{formatUserCount}</CardUsers>

        <ButtonContainer>
          <Stack direction="row" alignItems="center" gap={2}>
            Select Package
            {isEnterprise && (
              <AccessTextField
                sx={{ width: 60 }}
                value={licenseCount}
                onChange={handleLicenseCountChange}
                disabled={!isCurrentlySelected}
                inputProps={{
                  'aria-label': 'Number of enterprise users',
                  min: 1,
                  type: 'number',
                }}
              />
            )}
          </Stack>
          <Switch
            checked={isCurrentlySelected}
            onChange={handleToggleChange}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.light,
              },
            }}
            size="small"
            inputProps={{ 'aria-label': `Select ${type} package` }}
          />
        </ButtonContainer>

        <BodyContainer>
          {body.map((item, index) => (
            <Typography
              key={`${item}-${index}`}
              fontSize={14}
              fontWeight={600}
              color={getBodyItemColor(item)}
            >
              {item}
            </Typography>
          ))}
        </BodyContainer>

        {benefits.map((benefit, benefitIndex) => (
          <Stack py={1.5} key={`${benefit.label}-${benefitIndex}`}>
            <Typography fontSize={14} fontWeight={700}>
              {benefit.label}
            </Typography>
            {benefit.details.map((detail, detailIndex) => (
              <Typography
                fontSize={14}
                key={`${detail}-${detailIndex}`}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
              >
                <FontAwesomeIcon
                  icon={faCheck as IconProp}
                  size="sm"
                  color={theme.palette.primary.dark}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                {detail}
              </Typography>
            ))}
          </Stack>
        ))}
      </CardBody>
    </Stack>
  );
};
